import { ActivatedRoute, Router } from '@angular/router';
import { FeedbackContext } from './feedback.model';

/**
 * Readable entity type for the URL segment that immediately precedes a numeric route param.
 * Deliberately tiny — only covers the routes Feedback actually needs, not a generic mapping.
 */
const ENTITY_TYPE_BY_PRECEDING_SEGMENT: Record<string, string> = {
    'deal-details': 'Deal',
    edit: 'Deal'
};

/**
 * Builds the auto-captured "where did this come from" payload for a feedback submission.
 *
 * Reads only the route, the deepest matched route's title, and a numeric route param — never
 * DOM content or on-screen figures, per the plan's PII guardrail (no silent capture of visible
 * client financial data).
 */
export function buildFeedbackContext(router: Router, activatedRoute: ActivatedRoute): FeedbackContext {
    const route = router.url.split('?')[0];

    let leaf = activatedRoute.root.snapshot;
    while (leaf.firstChild) leaf = leaf.firstChild;

    const rawTitle = (leaf.routeConfig?.title as string) || (typeof document !== 'undefined' ? document.title : '') || '';
    const pageTitle = rawTitle.replace(/\s*\|\s*BettaAlpha\s*$/i, '').trim();

    const { entityType, entityId } = extractEntityFromRoute(route);

    return {
        route,
        pageTitle,
        entityType,
        entityId,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
    };
}

function extractEntityFromRoute(route: string): { entityType: string | null; entityId: number | null } {
    const segments = route.split('/').filter(Boolean);

    for (let i = 1; i < segments.length; i++) {
        const numeric = Number(segments[i]);
        if (segments[i] === '' || Number.isNaN(numeric)) continue;

        const mapped = ENTITY_TYPE_BY_PRECEDING_SEGMENT[segments[i - 1]];
        if (mapped) return { entityType: mapped, entityId: numeric };
    }

    return { entityType: null, entityId: null };
}
